import { useEffect, useState } from 'react';
import { useSEO } from '../../hooks/useSEO';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const AIRBNB_URL = 'https://www.airbnb.es/experiences/7159493';
const img = (n: string) => `/experiencia/${n}`;

function AirbnbButton({
  children,
  variant = 'primary',
  className = '',
}: {
  children: React.ReactNode;
  variant?: 'primary' | 'light' | 'outline';
  className?: string;
}) {
  const base =
    'inline-flex items-center justify-center gap-2 font-sans text-sm tracking-widest uppercase px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap';
  const styles =
    variant === 'primary'
      ? 'bg-brand-black text-white hover:bg-brand-gray-600'
      : variant === 'light'
        ? 'bg-white text-brand-black hover:bg-brand-sage'
        : 'border border-white/70 text-white hover:bg-white hover:text-brand-black';
  return (
    <a href={AIRBNB_URL} target="_blank" rel="noopener noreferrer" className={`${base} ${styles} ${className}`}>
      {children}
      <i className="ri-arrow-right-up-line text-base" />
    </a>
  );
}

const valueProps = [
  { icon: 'ri-group-line', title: 'Grupos reducidos', text: 'Una experiencia íntima y personal, no una clase masificada.' },
  { icon: 'ri-map-pin-2-line', title: 'Atelier real en Barcelona', text: 'En el espacio donde nacen las piezas de alta costura.' },
  { icon: 'ri-scissors-cut-line', title: 'Materiales incluidos', text: 'Telas, maniquí y herramientas listas para crear.' },
  { icon: 'ri-sparkling-line', title: 'Todos los niveles', text: 'Sin experiencia previa. Solo ganas de crear.' },
];

const steps = [
  {
    n: '01',
    img: 'exp-05.jpg',
    title: 'Bienvenida y recorrido privado',
    text: 'Anthony te recibe y te abre las puertas de su atelier: un recorrido íntimo entre maniquíes, telas y piezas en pleno proceso de creación.',
  },
  {
    n: '02',
    img: 'exp-13.jpg',
    title: 'Iniciación al moulage',
    text: 'Descubre la técnica que da origen a la alta costura: modelar la tela directamente sobre el maniquí, sin patrones, dejando que el tejido hable.',
  },
  {
    n: '03',
    img: 'exp-03.jpg',
    title: 'Manos a la obra',
    text: 'Con la guía de Anthony y su equipo, das forma a tu propia pieza y experimentas con volúmenes, drapeados y caídas.',
  },
  {
    n: '04',
    img: 'exp-11.jpg',
    title: 'El oficio de cerca',
    text: 'Conoce los secretos del atelier: costura, acabados y los pequeños gestos artesanales que distinguen a una prenda única.',
  },
];

const gallery = [
  'exp-01.jpg', 'exp-07.jpg', 'exp-04.jpg',
  'exp-10.jpg', 'exp-06.jpg', 'exp-16.jpg',
  'exp-08.jpg', 'exp-09.jpg',
];

export default function Experiencia() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  useSEO({
    title: 'Experiencia Airbnb | Crea tu Vestido de Alta Costura | Anthony Sánchez',
    description:
      'Vive un día en el atelier de Anthony Sánchez en Barcelona: recorrido privado y taller de moulage donde creas tu propia pieza de alta costura. Valorada 5,0 en Airbnb.',
  });

  return (
    <div className="w-full bg-white">
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative w-full h-[92vh] min-h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img src={img('exp-01.jpg')} alt="Atelier de Anthony Sánchez en Barcelona" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40" />
        </div>

        <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24 px-6 lg:px-[7%]">
          <div className={`transition-all duration-1000 ease-out ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-3 py-1.5 font-sans text-white text-[11px] tracking-[0.15em] uppercase">
                <i className="ri-map-pin-2-line" /> Experiencia Airbnb · Barcelona
              </span>
              <span className="inline-flex items-center gap-1 bg-white text-brand-black rounded-full px-3 py-1.5 font-sans text-[12px] font-semibold">
                <i className="ri-star-fill text-amber-500" /> 5,0
              </span>
            </div>
            <h1 className="font-serif text-white text-[clamp(2.6rem,7vw,5.5rem)] font-light leading-[1.05] max-w-4xl">
              Crea tu propio
              <span className="block italic">vestido de alta costura</span>
            </h1>
            <p className="font-sans text-white/85 text-base md:text-lg max-w-xl leading-relaxed mt-6">
              Un día dentro del atelier de Anthony Sánchez: recorrido privado por su espacio de trabajo
              y un taller intensivo de <em className="not-italic font-medium text-white">moulage</em> donde
              esculpes la tela directamente sobre el maniquí.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <AirbnbButton variant="light">Reservar en Airbnb</AirbnbButton>
              <a href="#experiencia" className="font-sans text-white/80 text-sm tracking-widest uppercase border-b border-white/40 pb-1 hover:text-white hover:border-white transition-colors">
                Descubre la experiencia
              </a>
            </div>
            <p className="font-sans text-white/60 text-xs mt-5 flex items-center gap-1.5">
              <i className="ri-shield-check-line" /> Reserva y pago 100% seguros en Airbnb · Plazas limitadas
            </p>
          </div>
        </div>
      </section>

      {/* ── VALUE PROPS ── */}
      <section id="experiencia" className="w-full bg-brand-cream py-14 md:py-16 px-6 lg:px-[7%]">
        <div className="max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
          {valueProps.map((v) => (
            <div key={v.title} className="text-center lg:text-left">
              <div className="w-12 h-12 rounded-full bg-brand-black text-white flex items-center justify-center mx-auto lg:mx-0">
                <i className={`${v.icon} text-xl`} />
              </div>
              <h3 className="font-serif text-brand-black text-lg mt-4">{v.title}</h3>
              <p className="font-sans text-brand-gray-600 text-sm leading-relaxed mt-1.5">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="w-full bg-white py-20 md:py-28 px-6 lg:px-[7%]">
        <div className="max-w-3xl mx-auto text-center">
          <span className="font-sans text-brand-gray-500 text-[11px] tracking-[0.2em] uppercase font-medium">La experiencia</span>
          <h2 className="font-serif text-brand-black text-[clamp(1.9rem,4vw,3rem)] leading-[1.2] mt-4">
            La alta costura, <span className="italic">vivida desde dentro</span>
          </h2>
          <p className="font-sans text-brand-gray-600 text-base md:text-lg leading-relaxed mt-6">
            No es una visita. Es ponerte la bata, coger la tela y crear con tus propias manos bajo la mirada
            de un diseñador de alta costura. Una tarde para desconectar del mundo y conectar con el oficio
            artesanal más exclusivo, en pleno corazón de Barcelona.
          </p>
        </div>
      </section>

      {/* ── STEPS ── */}
      <section className="w-full bg-brand-gray-50 py-20 md:py-28 px-6 lg:px-[7%]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="font-sans text-brand-gray-500 text-[11px] tracking-[0.2em] uppercase font-medium">Paso a paso</span>
            <h2 className="font-serif text-brand-black text-[clamp(1.9rem,4vw,3rem)] leading-[1.2] mt-4">Qué vivirás</h2>
          </div>

          <div className="space-y-10 md:space-y-16">
            {steps.map((s, i) => (
              <div key={s.n} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center ${i % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}>
                <div className="overflow-hidden rounded-[28px] [direction:ltr]">
                  <img src={img(s.img)} alt={s.title} className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="[direction:ltr]">
                  <span className="font-serif text-brand-sage text-5xl font-light">{s.n}</span>
                  <h3 className="font-serif text-brand-black text-2xl md:text-3xl font-medium mt-2">{s.title}</h3>
                  <p className="font-sans text-brand-gray-600 text-base leading-relaxed mt-4 max-w-md">{s.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <AirbnbButton>Ver fechas y reservar</AirbnbButton>
          </div>
        </div>
      </section>

      {/* ── GALLERY ── */}
      <section className="w-full bg-white py-20 md:py-28 px-6 lg:px-[7%]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-sans text-brand-gray-500 text-[11px] tracking-[0.2em] uppercase font-medium">El atelier</span>
            <h2 className="font-serif text-brand-black text-[clamp(1.9rem,4vw,3rem)] leading-[1.2] mt-4">Un vistazo por dentro</h2>
          </div>
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:balance]">
            {gallery.map((g) => (
              <div key={g} className="mb-4 overflow-hidden rounded-2xl break-inside-avoid">
                <img src={img(g)} alt="Experiencia en el atelier de Anthony Sánchez" loading="lazy" className="w-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOST ── */}
      <section className="w-full bg-brand-black py-20 md:py-28 px-6 lg:px-[7%]">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-[40%_60%] gap-10 md:gap-16 items-center">
          <div className="overflow-hidden rounded-[28px]">
            <img src={img('exp-02.jpg')} alt="Anthony Sánchez, diseñador de alta costura" className="w-full aspect-[3/4] object-cover" />
          </div>
          <div>
            <span className="font-sans text-brand-sage text-[11px] tracking-[0.2em] uppercase font-medium">Tu anfitrión</span>
            <h2 className="font-serif text-white text-[clamp(1.9rem,4vw,3rem)] leading-[1.15] mt-4">Anthony Sánchez</h2>
            <p className="font-sans text-white/70 text-base leading-relaxed mt-6">
              Diseñador de alta costura afincado en Barcelona, Anthony viste a novias, galas y clientas de todo
              el mundo desde su atelier. En esta experiencia abre sus puertas para compartir su oficio y contagiarte
              la pasión por la tela hecha a mano.
            </p>
            <div className="flex flex-wrap items-center gap-6 mt-8">
              <div className="flex items-center gap-2">
                <i className="ri-star-fill text-amber-400" />
                <span className="font-sans text-white text-sm">Valoración 5,0 en Airbnb</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-verified-badge-line text-brand-sage" />
                <span className="font-sans text-white text-sm">Experiencia verificada</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="relative w-full overflow-hidden">
        <img src={img('exp-13.jpg')} alt="Taller de moulage en el atelier" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-black/70" />
        <div className="relative z-10 max-w-3xl mx-auto text-center py-24 md:py-32 px-6">
          <h2 className="font-serif text-white text-[clamp(2rem,5vw,3.5rem)] leading-[1.15]">
            Regálate un día de <span className="italic">alta costura</span>
          </h2>
          <p className="font-sans text-white/80 text-base md:text-lg leading-relaxed mt-5 max-w-xl mx-auto">
            Plazas limitadas por sesión. Reserva tu lugar en el atelier y vive la experiencia que no se olvida.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <AirbnbButton variant="light">Reservar mi plaza en Airbnb</AirbnbButton>
            <AirbnbButton variant="outline">Ver disponibilidad</AirbnbButton>
          </div>
          <p className="font-sans text-white/55 text-xs mt-6 flex items-center justify-center gap-1.5">
            <i className="ri-lock-line" /> Reserva y pago gestionados de forma segura por Airbnb
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
